// Positioning for the inline FloatingToolbar.
//
// The bubble is portaled to <body> with `position: fixed` and centered on the
// selection via a CSS `translateX(-50%)`. These nominal dimensions decide the
// vertical flip and clamp the horizontal centre so the bubble never lands
// off-screen — same approach (and rationale) as mentionDropdownPosition.ts.
// The real width varies with which button groups are visible; we over-estimate
// so the clamp keeps the bubble fully on-screen rather than flush to the edge.
export const FLOATING_TOOLBAR_NOMINAL_WIDTH_PX = 460;
export const FLOATING_TOOLBAR_HEIGHT_PX = 40;
export const FLOATING_TOOLBAR_GAP_PX = 12;
const VIEWPORT_MARGIN_PX = 8;

export type FloatingToolbarPlacement = 'above' | 'below';

export interface FloatingToolbarPosition {
    left: number;
    top: number;
    placement: FloatingToolbarPlacement;
}

interface AnchorRect {
    top: number;
    bottom: number;
    centerX: number;
}

interface Viewport {
    width: number;
    height: number;
}

interface ToolbarSize {
    width: number;
    height: number;
}

/**
 * Place the bubble relative to the selection's bounding rect.
 *
 * Prefers ABOVE the selection (the editor's natural reading position); flips
 * BELOW only when there isn't room for the bubble + gap above the selection
 * top — which is exactly the case that used to render it off-screen and
 * unreachable. Horizontally, returns a `left` (the CSS-centred anchor) clamped
 * so neither edge of the bubble crosses the viewport margin.
 */
export function computeFloatingToolbarPosition(
    anchor: AnchorRect,
    viewport: Viewport,
    size: ToolbarSize = { width: FLOATING_TOOLBAR_NOMINAL_WIDTH_PX, height: FLOATING_TOOLBAR_HEIGHT_PX },
    gap: number = FLOATING_TOOLBAR_GAP_PX,
): FloatingToolbarPosition {
    const placement: FloatingToolbarPlacement = anchor.top - gap - size.height >= VIEWPORT_MARGIN_PX ? 'above' : 'below';
    const top = placement === 'above' ? anchor.top : anchor.bottom;

    const half = size.width / 2;
    const minCenter = VIEWPORT_MARGIN_PX + half;
    const maxCenter = viewport.width - VIEWPORT_MARGIN_PX - half;
    // When the bubble is wider than the viewport, minCenter > maxCenter — pin to
    // the viewport centre rather than producing an inverted clamp range.
    const left = maxCenter >= minCenter ? Math.min(Math.max(anchor.centerX, minCenter), maxCenter) : viewport.width / 2;

    return { left, top, placement };
}
